import { Link } from "react-router-dom";
import {
  Webhook as WebhookIcon,
  ShieldCheck,
  KeyRound,
  Send,
  RotateCw,
  CheckCircle2,
  Crown,
  ArrowUpRight,
  Copy,
  Check,
  AlertTriangle,
  Hash,
} from "lucide-react";
import { useState } from "react";
import SEO from "@/components/SEO";
import DashboardHeader from "@/components/DashboardHeader";
import DashboardFooter from "@/components/DashboardFooter";
import { useOptionalMerchant } from "@/lib/useMerchant";
import BackToDashboard from "@/components/BackToDashboard";

/**
 * WebhooksGuide.jsx
 * ------------------------------------------------------------------
 * Companion to RazorpayKeysGuide.jsx / TeamGuide.jsx, but for the
 * Webhooks (Pro) feature.
 *
 * v2: payload is now enriched (order/return/customer/item detail,
 * not just IDs) and — importantly — NOT perfectly uniform across
 * events. This version documents the real, verified shape per event
 * instead of implying one universal schema, since a couple of fields
 * are known to be absent on specific events (see FIELD_NOTES).
 *
 * Deliberately NOT wrapped in RequireOwner — same reasoning as
 * TeamGuide: useful to read before upgrading, nothing sensitive here.
 * ------------------------------------------------------------------
 */

const EVENTS = [
  { key: "order.created", desc: "A new order is placed, before payment. No order_number or status yet — those are assigned once paid." },
  { key: "order.paid", desc: "Payment confirmed — via Razorpay, Stripe, PayPal, or manual/UPI." },
  { key: "order.shipped", desc: "You mark the order as shipped from the dashboard. Includes shipped_at." },
  { key: "return.requested", desc: "A customer asks for a return, from the dashboard or Instagram DM." },
  { key: "return.approved", desc: "You approve a return request. Includes merchant_note if you added one." },
  { key: "return.rejected", desc: "You reject a return request. Includes merchant_note if you added one." },
];

const FIELD_NOTES = [
  { field: "order_number", note: "Absent on order.created — assigned right after payment, so it isn't ready yet at that point." },
  { field: "status", note: "Present on order.paid only (\"paid\"). Not included on order.created or order.shipped — infer state from the event name itself." },
  { field: "customer_email", note: "May be absent when a return is requested over Instagram DM, since email isn't always captured in that flow. Always present on order events." },
  { field: "shipped_at", note: "Only present on order.shipped." },
  { field: "reason", note: "Only present on the three return.* events — the customer's stated reason at request time." },
  { field: "merchant_note", note: "Only present on return.approved / return.rejected — whatever you typed when deciding, or null if you left it blank." },
  { field: "discount", note: "Always present as a key, but its value is null whenever no coupon was applied — not omitted." },
  { field: "items", note: "For return events, this reflects only the specific items being returned when that data is available, falling back to the full order's items otherwise." },
];

function CodeBlock({ code, lang = "js" }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="relative overflow-hidden rounded-2xl border border-ink/10 bg-ink text-cream">
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-2">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-cream/50">{lang}</span>
        <button onClick={copy} className="flex items-center gap-1.5 text-[11px] font-semibold text-cream/60 hover:text-cream">
          {copied ? <Check size={12} className="text-mint" /> : <Copy size={12} />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="overflow-x-auto px-4 py-4 text-[12.5px] leading-relaxed">
        <code>{code}</code>
      </pre>
    </div>
  );
}

function StepCard({ n, icon: Icon, title, desc }) {
  return (
    <div className="relative rounded-2xl border border-ink/10 bg-white p-5">
      <div className="flex items-start gap-3.5">
        <span className="grid h-10 w-10 flex-none place-items-center rounded-2xl bg-mint text-ink font-display text-sm font-black">
          {n}
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Icon size={15} className="text-ink/50" />
            <p className="font-bold text-ink">{title}</p>
          </div>
          <p className="mt-1.5 text-[13px] leading-relaxed text-ink/70">{desc}</p>
        </div>
      </div>
    </div>
  );
}

const verifySnippetNode = `const crypto = require('crypto');

function isValidSignature(rawBody, signatureHeader, secret) {
  const expected = crypto
    .createHmac('sha256', secret)
    .update(rawBody)
    .digest('hex');

  // constant-time compare
  return crypto.timingSafeEqual(
    Buffer.from(expected),
    Buffer.from(signatureHeader)
  );
}

// Express example
app.post('/checkoutdm-hook', express.raw({ type: '*/*' }), (req, res) => {
  const signature = req.headers['x-checkoutdm-signature'];
  const secret = process.env.CHECKOUTDM_WEBHOOK_SECRET;

  if (!isValidSignature(req.body, signature, secret)) {
    return res.status(401).send('invalid signature');
  }

  const event = JSON.parse(req.body);
  // event.event, event.business_id, event.entity_id — always present.
  // Everything else depends on event type; see "Field notes" below.
  res.status(200).send('ok');
});`;

const payloadSampleOrderPaid = `{
  "event": "order.paid",
  "business_id": 4821,
  "entity_id": 91045,
  "timestamp": "2026-08-24T06:24:06.712Z",
  "order_number": "CDM-000044",
  "customer_name": "Jane Doe",
  "customer_phone": "+919876543210",
  "customer_email": "jane@example.com",
  "address": {
    "raw": "221B Baker Street, London, NW1 6XE",
    "line1": "221B Baker Street",
    "line2": "",
    "city": "London",
    "state": "",
    "country": "UK",
    "pincode": "NW1 6XE"
  },
  "total_amount": "1148",
  "currency": "INR",
  "status": "paid",
  "shipping_fee": "150",
  "discount": null,
  "items": [
    { "name": "Blue Washed Jeans for Men", "qty": 2, "price": 499, "size": "M" }
  ]
}`;

const payloadSampleReturnApproved = `{
  "event": "return.approved",
  "business_id": 4821,
  "entity_id": 31,
  "timestamp": "2026-08-24T06:36:05.260Z",
  "order_number": "CDM-000045",
  "customer_name": "Jane Doe",
  "customer_phone": "+919876543210",
  "customer_email": "jane@example.com",
  "address": { "raw": "...", "line1": "...", "line2": "", "city": "...", "state": "...", "country": "...", "pincode": "..." },
  "total_amount": "1949",
  "currency": "INR",
  "shipping_fee": "150",
  "discount": null,
  "reason": "Wrong size ordered, needs return now",
  "merchant_note": null,
  "items": [
    { "name": "Red T-Shirt", "qty": 2, "price": 1, "size": "S" }
  ]
}`;

export default function WebhooksGuide() {
  const { me } = useOptionalMerchant();
  const isPro = me?.plan === "pro";

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline: "How Checkout DM webhooks work",
    description: "A guide to setting up and verifying Checkout DM webhooks for orders and returns.",
    publisher: { "@type": "Organization", name: "Checkout DM", url: "https://checkoutdm.com" },
    mainEntityOfPage: "https://checkoutdm.com/webhooks-guide",
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://checkoutdm.com/" },
      { "@type": "ListItem", position: 2, name: "Webhooks Guide", item: "https://checkoutdm.com/webhooks-guide" },
    ],
  };

  return (
    <div className="min-h-screen bg-cream">
      <SEO
        title="Webhooks Guide | Checkout DM"
        description="How Checkout DM webhooks work — events, payload fields, signing, verification, and retries."
        path="/webhooks-guide"
        structuredData={[articleSchema, breadcrumbSchema]}
      />
      <DashboardHeader me={me} />

      <main className="mx-auto max-w-4xl px-6 pt-10 md:px-10">
        {me && <BackToDashboard />}

        <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Help · Webhooks</p>
        <h1 className="mt-2 font-display text-4xl font-black tracking-tighter md:text-5xl">
          Connecting your own system with webhooks
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-ink/70 md:text-base">
          Webhooks push order and return events — with full order, customer, and item detail —
          from Checkout DM to your own CRM, ERP, or automation stack the moment they happen. No
          polling required.
        </p>

        {!isPro && (
          <div className="mt-6 flex items-start gap-3 rounded-2xl border border-hotpink/20 bg-hotpink/5 p-4">
            <Crown size={18} className="mt-0.5 flex-none text-hotpink" />
            <div className="text-sm text-ink/70">
              Webhooks are a <strong>Pro plan</strong> feature.{" "}
              <Link to="/plans" className="inline-flex items-center gap-1 font-semibold text-hotpink hover:underline">
                Upgrade to Pro <ArrowUpRight size={12} />
              </Link>
            </div>
          </div>
        )}

        {/* Step 1 */}
        <h2 className="mt-10 font-display text-xl font-black tracking-tight">Set it up</h2>
        <div className="mt-4 grid gap-3">
          <StepCard
            n={1}
            icon={WebhookIcon}
            title="Add your endpoint"
            desc={
              <>Go to <Link to="/webhooks" className="font-semibold text-hotpink hover:underline">Webhooks</Link> in your dashboard, add your HTTPS endpoint URL, and pick which events you want. Up to 10 webhooks per account.</>
            }
          />
          <StepCard
            n={2}
            icon={KeyRound}
            title="Save your signing secret"
            desc="You'll see it once, right after creating the webhook. Store it in your own environment variables — Checkout DM never shows it again."
          />
          <StepCard
            n={3}
            icon={Send}
            title="Receive events"
            desc="From then on, matching events are POSTed to your URL in real time as they happen in your Checkout DM dashboard or over Instagram DM."
          />
        </div>

        {/* Events */}
        <h2 className="mt-10 font-display text-xl font-black tracking-tight">Available events</h2>
        <div className="mt-4 overflow-hidden rounded-2xl border border-ink/10 bg-white">
          <ul className="divide-y divide-ink/10">
            {EVENTS.map((e) => (
              <li key={e.key} className="flex flex-col gap-1 px-5 py-3.5 sm:flex-row sm:items-start sm:gap-4">
                <code className="flex-none rounded-lg bg-ink/5 px-2.5 py-1 text-xs font-semibold text-ink sm:w-44">
                  {e.key}
                </code>
                <span className="text-sm text-ink/65">{e.desc}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Payload */}
        <h2 className="mt-10 font-display text-xl font-black tracking-tight">Payload shape</h2>
        <p className="mt-2 text-sm text-ink/65">
          <code className="rounded bg-ink/5 px-1.5 py-0.5 text-xs">event</code>,{" "}
          <code className="rounded bg-ink/5 px-1.5 py-0.5 text-xs">business_id</code>,{" "}
          <code className="rounded bg-ink/5 px-1.5 py-0.5 text-xs">entity_id</code>, and{" "}
          <code className="rounded bg-ink/5 px-1.5 py-0.5 text-xs">timestamp</code> are always present.
          Everything else — order, customer, address, and item detail — is included where it's
          relevant and available, but the exact field set varies by event type. Below are two real
          examples; see "Field notes" further down for what differs and why.
        </p>
        <p className="mt-3 text-sm font-semibold text-ink">order.paid</p>
        <div className="mt-2">
          <CodeBlock code={payloadSampleOrderPaid} lang="json" />
        </div>
        <p className="mt-4 text-sm font-semibold text-ink">return.approved</p>
        <div className="mt-2">
          <CodeBlock code={payloadSampleReturnApproved} lang="json" />
        </div>

        <div className="mt-4 flex items-start gap-2.5 rounded-2xl bg-peach/30 p-4 text-sm text-ink/70">
          <AlertTriangle size={16} className="mt-0.5 flex-none text-ink/50" />
          <span>
            Amounts (<code className="rounded bg-white/60 px-1 py-0.5 text-xs">total_amount</code>,{" "}
            <code className="rounded bg-white/60 px-1 py-0.5 text-xs">shipping_fee</code>) are sent as{" "}
            <strong>strings</strong>, not numbers — this avoids floating-point rounding on money
            values. Item <code className="rounded bg-white/60 px-1 py-0.5 text-xs">price</code> is a
            plain number. Parse amounts explicitly rather than assuming a type.
          </span>
        </div>

        {/* Field notes */}
        <h2 className="mt-10 flex items-center gap-2 font-display text-xl font-black tracking-tight">
          <Hash size={18} className="text-ink/50" /> Field notes
        </h2>
        <p className="mt-2 text-sm text-ink/65">
          Write your integration defensively — check a field exists before reading it, rather than
          assuming every field appears on every event.
        </p>
        <div className="mt-4 overflow-hidden rounded-2xl border border-ink/10 bg-white">
          <ul className="divide-y divide-ink/10">
            {FIELD_NOTES.map((f) => (
              <li key={f.field} className="flex flex-col gap-1 px-5 py-3.5 sm:flex-row sm:items-start sm:gap-4">
                <code className="flex-none rounded-lg bg-ink/5 px-2.5 py-1 text-xs font-semibold text-ink sm:w-40">
                  {f.field}
                </code>
                <span className="text-sm text-ink/65">{f.note}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Signature verification */}
        <h2 className="mt-10 flex items-center gap-2 font-display text-xl font-black tracking-tight">
          <ShieldCheck size={18} className="text-mint" /> Verifying signatures
        </h2>
        <p className="mt-2 text-sm text-ink/65">
          Every request carries an <code className="rounded bg-ink/5 px-1.5 py-0.5 text-xs">X-CheckoutDM-Signature</code>{" "}
          header — an HMAC-SHA256 hash of the raw request body, signed with your webhook secret.
          Always verify it before trusting a payload, using the raw bytes exactly as received —
          even a single extra space will produce a different hash.
        </p>
        <div className="mt-3">
          <CodeBlock code={verifySnippetNode} lang="node.js" />
        </div>

        {/* Retries */}
        <h2 className="mt-10 flex items-center gap-2 font-display text-xl font-black tracking-tight">
          <RotateCw size={18} className="text-hotpink" /> Retries & failed deliveries
        </h2>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-ink/65">
          If your endpoint doesn't respond with a 2xx status, we retry automatically for a few
          attempts. After that, the delivery stops retrying on its own — but it isn't lost. Head to{" "}
          <Link to="/webhooks" className="font-semibold text-hotpink hover:underline">Webhooks</Link> in
          your dashboard and you'll see it listed under "Failed deliveries," grouped by which
          webhook it belongs to, with a one-click <strong>Resend</strong> once your endpoint is back
          up. Make sure your endpoint returns a 2xx quickly (ideally under a few seconds) once it's
          accepted the event, and do any slow processing afterwards on your side.
        </p>

        <div className="mb-16 mt-10 flex items-center gap-2 rounded-2xl bg-mint/20 p-4 text-sm text-ink/70">
          <CheckCircle2 size={16} className="flex-none text-ink/50" />
          Questions about a specific delivery? Reach out to support with the event type and
          approximate time and we can look it up on our side.
        </div>
      </main>

      <DashboardFooter />
    </div>
  );
}
